(() => {
  'use strict';

  const ELEMENT_NAME =
    'deeb-product-recommendations';

  const LOADED_ATTRIBUTE =
    'data-deeb-recommendations-loaded';

  const STATE_ATTRIBUTE =
    'data-deeb-recommendations-state';

  const OBSERVER_FALLBACK_DELAY = 1200;
  const RETRY_DELAY = 500;
  const MAX_LOAD_ATTEMPTS = 2;

  /*
   * Shopify Theme Editor can evaluate assets more than once.
   * A unique custom-element name plus this guard prevents
   * duplicate registration and keeps Dawn's native
   * product-recommendations element completely separate.
   */
  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class DeebProductRecommendations
    extends HTMLElement {
    constructor() {
      super();

      this.isLoading = false;
      this.isLoaded = false;
      this.loadAttempts = 0;

      this.observer = null;
      this.revealObserver = null;
      this.abortController = null;
      this.observerFallbackTimer = null;
      this.retryTimer = null;
    }

    connectedCallback() {
      if (this.hasRenderedContent()) {
        this.refreshWishlist();
        this.prepareReveal();

        if (!this.hasProvisionalContent()) {
          this.markLoaded();
          return;
        }
      }

      if (this.isLoaded || this.isLoading) {
        return;
      }

      if (this.hasProvisionalContent()) {
        this.loadRecommendations();
        return;
      }

      this.startLoadingObserver();
    }

    disconnectedCallback() {
      this.disconnectObserver();
      this.disconnectRevealObserver();
      this.clearObserverFallback();
      this.clearRetry();
      this.abortRequest();
    }

    hasRenderedContent() {
      return Boolean(
        this.querySelector(
          '.deeb-related-cigars__inner'
        )
      );
    }

    hasProvisionalContent() {
      return (
        this.dataset
          .deebRelatedProvisional ===
        'true'
      );
    }

    prepareReveal() {
      this.disconnectRevealObserver();

      const content = this.querySelector(
        '.deeb-related-cigars__inner'
      );

      if (!content) {
        return;
      }

      const revealTargets = [
        content.querySelector(
          '.deeb-related-cigars__header'
        ),
        ...content.querySelectorAll(
          '.deeb-related-cigars__item'
        ),
      ].filter(Boolean);

      revealTargets.forEach(
        (target, index) => {
          target.style.setProperty(
            '--deeb-related-reveal-index',
            index
          );
        }
      );

      content.dataset.deebRevealState =
        'pending';

      const reveal = () => {
        content.dataset.deebRevealState =
          'visible';
      };

      if (
        !('IntersectionObserver' in window) ||
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        window.requestAnimationFrame(reveal);
        return;
      }

      this.revealObserver =
        new IntersectionObserver(
          (entries) => {
            if (
              !entries.some(
                (entry) =>
                  entry.isIntersecting
              )
            ) {
              return;
            }

            this.disconnectRevealObserver();
            reveal();
          },
          { threshold: 0.08 }
        );

      this.revealObserver.observe(content);
    }

    disconnectRevealObserver() {
      if (!this.revealObserver) {
        return;
      }

      this.revealObserver.disconnect();
      this.revealObserver = null;
    }

    setState(state) {
      this.setAttribute(
        STATE_ATTRIBUTE,
        state
      );
    }

    markLoaded() {
      this.isLoaded = true;
      this.setState('loaded');
      delete this.dataset
        .deebRelatedProvisional;

      this.setAttribute(
        LOADED_ATTRIBUTE,
        'true'
      );
    }

    startLoadingObserver() {
      this.disconnectObserver();
      this.clearObserverFallback();
      this.setState('observing');

      if (!('IntersectionObserver' in window)) {
        this.loadRecommendations();
        return;
      }

      this.observer =
        new IntersectionObserver(
          (entries) => {
            const isVisible = entries.some(
              (entry) => entry.isIntersecting
            );

            if (!isVisible) {
              return;
            }

            this.disconnectObserver();
            this.clearObserverFallback();
            this.loadRecommendations();
          },
          {
            rootMargin:
              '0px 0px 1000px 0px',
          }
        );

      this.observer.observe(this);

      this.observerFallbackTimer =
        window.setTimeout(() => {
          this.observerFallbackTimer = null;

          if (
            !this.isConnected ||
            this.isLoaded ||
            this.isLoading
          ) {
            return;
          }

          this.disconnectObserver();
          this.loadRecommendations();
        }, OBSERVER_FALLBACK_DELAY);
    }

    disconnectObserver() {
      if (!this.observer) {
        return;
      }

      this.observer.disconnect();
      this.observer = null;
    }

    clearObserverFallback() {
      if (!this.observerFallbackTimer) {
        return;
      }

      window.clearTimeout(
        this.observerFallbackTimer
      );

      this.observerFallbackTimer = null;
    }

    clearRetry() {
      if (!this.retryTimer) {
        return;
      }

      window.clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }

    abortRequest() {
      if (!this.abortController) {
        return;
      }

      this.abortController.abort();
      this.abortController = null;
    }

    buildRequestUrl() {
      const requestPath = String(
        this.dataset.url || ''
      ).trim();

      if (!requestPath) {
        return null;
      }

      const requestUrl = new URL(
        requestPath,
        window.location.origin
      );

      const sectionId = String(
        this.dataset.sectionId || ''
      ).trim();

      const productId = String(
        this.dataset.productId || ''
      ).trim();

      if (sectionId) {
        requestUrl.searchParams.set(
          'section_id',
          sectionId
        );
      }

      if (productId) {
        requestUrl.searchParams.set(
          'product_id',
          productId
        );
      }

      if (
        !requestUrl.searchParams.has(
          'intent'
        )
      ) {
        requestUrl.searchParams.set(
          'intent',
          'related'
        );
      }

      return requestUrl;
    }

    findRenderedElement(parsedDocument) {
      if (this.id) {
        const matchingElement =
          parsedDocument.getElementById(
            this.id
          );

        if (
          matchingElement?.localName ===
          ELEMENT_NAME
        ) {
          return matchingElement;
        }
      }

      const sectionId = String(
        this.dataset.sectionId || ''
      ).trim();

      if (sectionId) {
        const matchingSection =
          parsedDocument.querySelector(
            `${ELEMENT_NAME}[data-section-id="${CSS.escape(
              sectionId
            )}"]`
          );

        if (matchingSection) {
          return matchingSection;
        }
      }

      return parsedDocument.querySelector(
        ELEMENT_NAME
      );
    }

    getFallbackContent() {
      const fallbackTemplate =
        this.querySelector(
          'template[data-deeb-related-fallback]'
        );

      if (!fallbackTemplate) {
        return null;
      }

      const fallbackFragment =
        fallbackTemplate.content.cloneNode(
          true
        );

      return fallbackFragment.querySelector(
        '.deeb-related-cigars__inner'
      );
    }

    getRecommendationContent(
      sourceElement
    ) {
      if (!sourceElement) {
        return null;
      }

      const sourceContent =
        sourceElement.querySelector(
          '.deeb-related-cigars__inner'
        );

      return sourceContent
        ? sourceContent.cloneNode(true)
        : null;
    }

    getRenderedItemCount(scope) {
      if (!scope) {
        return 0;
      }

      return scope.querySelectorAll(
        '.deeb-related-cigars__item'
      ).length;
    }

    renderContent(content) {
      if (!content) {
        return false;
      }

      this.clearObserverFallback();
      this.clearRetry();

      const currentItemCount =
        this.getRenderedItemCount(this);
      const incomingItemCount =
        this.getRenderedItemCount(content);

      /*
       * The server renders a complete Cigar-only fallback
       * immediately. Do not replace that stable rail with a
       * smaller automatic recommendation result after filtering.
       */
      if (
        currentItemCount > 0 &&
        incomingItemCount > 0 &&
        incomingItemCount < currentItemCount
      ) {
        this.markLoaded();
        this.removeAttribute('aria-busy');
        this.refreshWishlist();
        return true;
      }

      this.replaceChildren(content);
      this.markLoaded();
      this.removeAttribute('aria-busy');

      this.refreshWishlist();
      this.prepareReveal();

      return true;
    }

    renderFallback() {
      const fallbackContent =
        this.getFallbackContent();

      if (!fallbackContent) {
        return false;
      }

      return this.renderContent(
        fallbackContent
      );
    }

    refreshWishlist() {
      const refresh = () => {
        if (
          window.DeebWishlist &&
          typeof window.DeebWishlist
            .refresh === 'function'
        ) {
          window.DeebWishlist.refresh(this);
        }

        document.dispatchEvent(
          new CustomEvent(
            'deeb:wishlist:refresh',
            {
              detail: {
                scope: this,
              },
            }
          )
        );
      };

      refresh();

      window.requestAnimationFrame(() => {
        refresh();
      });
    }

    handleNoContent() {
      if (this.hasRenderedContent()) {
        this.markLoaded();
        this.removeAttribute('aria-busy');
        return;
      }

      if (
        this.isConnected &&
        this.loadAttempts < MAX_LOAD_ATTEMPTS
      ) {
        this.clearRetry();
        this.setState('retrying');

        this.retryTimer = window.setTimeout(
          () => {
            this.retryTimer = null;
            this.loadRecommendations();
          },
          RETRY_DELAY
        );

        return;
      }

      if (this.renderFallback()) {
        return;
      }

      this.setState('empty');
      this.remove();
    }

    async loadRecommendations() {
      if (this.isLoading || this.isLoaded) {
        return;
      }

      this.clearObserverFallback();
      this.clearRetry();

      const productId = String(
        this.dataset.productId || ''
      ).trim();

      const requestUrl =
        this.buildRequestUrl();

      if (!productId || !requestUrl) {
        this.loadAttempts =
          MAX_LOAD_ATTEMPTS;
        this.handleNoContent();
        return;
      }

      this.loadAttempts += 1;
      this.isLoading = true;
      this.setState('loading');
      this.setAttribute(
        'aria-busy',
        'true'
      );

      this.abortRequest();

      this.abortController =
        new AbortController();

      try {
        const response = await fetch(
          requestUrl.toString(),
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              'X-Requested-With':
                'XMLHttpRequest',
            },
            signal:
              this.abortController.signal,
          }
        );

        if (!response.ok) {
          throw new Error(
            `Recommendations request failed with status ${response.status}.`
          );
        }

        const responseText =
          await response.text();

        const parsedDocument =
          new DOMParser().parseFromString(
            responseText,
            'text/html'
          );

        const sourceElement =
          this.findRenderedElement(
            parsedDocument
          );

        const recommendationContent =
          this.getRecommendationContent(
            sourceElement
          );

        if (
          !this.renderContent(
            recommendationContent
          )
        ) {
          this.handleNoContent();
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        console.warn(
          'Deeb related cigars could not be loaded.',
          {
            attempt: this.loadAttempts,
            requestUrl:
              requestUrl.toString(),
            error,
          }
        );

        this.handleNoContent();
      } finally {
        this.isLoading = false;
        this.abortController = null;

        if (this.isConnected) {
          this.removeAttribute(
            'aria-busy'
          );
        }
      }
    }
  }

  customElements.define(
    ELEMENT_NAME,
    DeebProductRecommendations
  );
})();
